import { Component, ErrorInfo, ReactNode } from 'react';
import { isDevelopment } from '@/shared/lib/env';
import { monitoringService } from '@/shared/services';
import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?:
    | ReactNode
    | ((error: Error, reset: () => void) => ReactNode);
  readonly onError?: (error: Error, errorInfo: ErrorInfo) => void;
  readonly onReset?: () => void;
  /**
   * Changing this value clears a caught error WITHOUT remounting `children`.
   *
   * The obvious way to make a boundary forget an error is `key` — but `key`
   * on a boundary that wraps a route tree remounts that whole tree on every
   * change, and the natural value to key on is the current pathname, so every
   * navigation would throw away layout state and re-run every effect. This
   * prop separates the two: `children` keep their identity, and only the
   * boundary's own error state is discarded when the caller says the context
   * that produced the error is gone (see `RouteErrorBoundary` in
   * `src/app/routes/AppRouter.tsx`, which passes the pathname).
   *
   * `onReset` is deliberately NOT called here: it is the user pressing
   * "Опитай отново", not the error becoming irrelevant on its own.
   */
  readonly resetKey?: string | number;
  /**
   * Tag recorded on the monitoring event, so a report says WHICH boundary
   * caught the throw. There is more than one now: the outermost one in
   * `main.tsx` (`'root'`, the default) plus the route- and layout-scoped ones
   * in `AppRouter.tsx`.
   */
  readonly boundaryName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component for catching React errors in child components
 *
 * Features:
 * - Catches JavaScript errors anywhere in child component tree
 * - Logs error information for debugging
 * - Displays fallback UI instead of crashing the whole app
 * - Provides reset functionality to recover from errors
 * - Optional custom fallback component
 * - Optional error callback for logging to external services
 * - Optional `resetKey` that clears a caught error on context change
 *   (e.g. a route navigation) without remounting the children
 *
 * Placement matters as much as the component. A boundary catches only what
 * renders BELOW it, so the one in `main.tsx` — which sits above
 * `BrowserRouter` — turns any render-time throw on any page into a
 * full-screen fallback with the router itself gone. `AppRouter.tsx` therefore
 * adds two more inside the router: one around the route table and one inside
 * the main layout's outlet, so a single page can fail without taking the
 * header, the navigation and the user's ability to leave the broken route
 * with it.
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <ErrorBoundary fallback={(error, reset) => <CustomError error={error} onReset={reset} />}>
 *   <SomeComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With error logging
 * <ErrorBoundary onError={(error, errorInfo) => logErrorToService(error, errorInfo)}>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error details in development
    if (isDevelopment) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Capture React errors in monitoring tools
    monitoringService.captureError(error, {
      tags: {
        type: 'react_error',
        boundary: this.props.boundaryName ?? 'root',
      },
      extra: {
        componentStack: errorInfo.componentStack || 'N/A',
      },
      level: 'error',
    });

    // Call optional error callback (for error logging services like Sentry)
    this.props.onError?.(error, errorInfo);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // A caught error belongs to the context it was thrown in. Once the caller
    // signals that context is gone — a navigation, in the router's case — the
    // fallback is stale and must clear itself, otherwise the boundary keeps
    // showing "Нещо се обърка" over a perfectly healthy new subtree.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  resetErrorBoundary = (): void => {
    // Call optional reset callback
    this.props.onReset?.();

    // Reset error state
    this.setState({
      hasError: false,
      error: null,
    });
  };

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        const fallback = this.props.fallback;
        // Check if fallback is a function
        if (typeof fallback === 'function') {
          return fallback(this.state.error, this.resetErrorBoundary);
        }
        // Otherwise it's a ReactNode
        return fallback;
      }

      // Render default fallback
      return (
        <ErrorFallback
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}
